import { Hourglass, Loader2 } from 'lucide-react'
import { Badge } from '@/components/StatusBadge'
import type { BadgeTone } from '@/components/StatusBadge'
import SyncTypeSelect from '@/components/SyncTypeSelect'
import { SYNC_TYP_DEFAULT, type SyncTyp } from '@/lib/syncTypes'
import { cn } from '@/lib/utils'

function ToggleSwitch({
  checked,
  disabled,
  title,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  title?: string
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-[18px] w-8 shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-emerald-500' : 'bg-zinc-300',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cn(
          'inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-[14px]' : 'translate-x-0.5',
        )}
      />
    </button>
  )
}

const FLAGS: Record<string, string> = {
  DE: '🇩🇪',
  UK: '🇬🇧',
  GB: '🇬🇧',
  FR: '🇫🇷',
  IT: '🇮🇹',
  NL: '🇳🇱',
  SE: '🇸🇪',
  TR: '🇹🇷',
  PL: '🇵🇱',
  ES: '🇪🇸',
  US: '🇺🇸',
  AU: '🇦🇺',
  JP: '🇯🇵',
  CA: '🇨🇦',
  MX: '🇲🇽',
  BR: '🇧🇷',
  IN: '🇮🇳',
}

export type ProfileSyncEntry = {
  id: number
  name: string
  marketplace: string
  statusTone: BadgeTone
  statusLabel: string
  lastSyncRelative: string
  entitiesLast: number | null
  isActive: boolean
}

type Props = {
  profiles: ProfileSyncEntry[]
  /** Profile, fuer die gerade eine START-ANFRAGE laeuft (mehrere zugleich). */
  startendeProfile: ReadonlySet<number>
  togglingId: number | null
  /**
   * Der TYP gehoert zum Klick, nicht zur Seite: Wer in dieser Zeile auf „Sync"
   * drueckt, meint den Typ, der in DIESER Zeile steht.
   */
  onSync: (profileId: number, typ: SyncTyp) => void
  /**
   * Laeuft fuer dieses Profil gerade ein Sync? Nicht dasselbe wie
   * `startendeProfile`: das Backend startet den Worker asynchron und antwortet
   * SOFORT, die Start-Anfrage endet also mit der Antwort — nicht mit dem Lauf
   * (R1-Befund). Diese Menge
   * kommt aus den geladenen Laeufen und bleibt gesetzt, solange dort ein
   * `running` steht.
   */
  laufendeProfile?: ReadonlySet<number>
  /**
   * Profile, deren START BEANTWORTET ist, aber noch von keiner Historie
   * bestaetigt wurde (zweite Sperrstufe der Sync-Seite). Sie sind gesperrt wie
   * `startendeProfile`, zeigen aber KEIN Ladezeichen: Die Anfrage laeuft nicht
   * mehr — die Seite wartet auf die naechste Liste. Ein Ladezeichen fuer eine
   * laengst beantwortete Anfrage war der Registerpunkt
   * SYNC-ZWEITE-STUFE-ZEIGT-STARTZEICHEN (PR #56). Laeuft die Anfrage noch
   * (Sammellauf), gewinnt `startendeProfile`.
   */
  unbestaetigteProfile?: ReadonlySet<number>
  /** Aktuelle Wahl je Profil. Fehlt ein Eintrag, gilt der Vorgabewert. */
  syncTypen: Record<number, SyncTyp>
  onSyncTypChange: (profileId: number, typ: SyncTyp) => void
  /**
   * Laeuft gerade ein SAMMELLAUF? Dann ist jede Zeile gesperrt (R2-Befund).
   * Sonst koennte man mitten im Lauf denselben Profilen einen zweiten Sync
   * schicken — das Backend haelt zwar dagegen, aber die Historie waere danach
   * nicht mehr lesbar, und die Wahl liesse sich waehrend des Laufens
   * verstellen.
   */
  bulkSyncing?: boolean
  onToggleActive: (profileId: number, next: boolean) => void
  className?: string
}

export default function ProfileSyncGrid({
  profiles,
  startendeProfile,
  togglingId,
  onSync,
  syncTypen,
  onSyncTypChange,
  bulkSyncing = false,
  laufendeProfile,
  unbestaetigteProfile,
  onToggleActive,
  className,
}: Props) {
  if (profiles.length === 0) {
    return (
      <div className="px-5 py-8 text-center text-sm text-[var(--muted-foreground)]">
        Keine Profile geladen — bitte LWA in den Einstellungen verknüpfen.
      </div>
    )
  }

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-px bg-[var(--border)] sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {profiles.map((p) => {
        const isSyncing = startendeProfile.has(p.id)
        const wartetAufBestaetigung = unbestaetigteProfile?.has(p.id) ?? false
        const laeuft = isSyncing || wartetAufBestaetigung || (laufendeProfile?.has(p.id) ?? false)
        const isToggling = togglingId === p.id
        const flag = FLAGS[p.marketplace.toUpperCase()] ?? '🌐'
        return (
          <div
            key={p.id}
            className={cn(
              'flex items-center gap-3 bg-[var(--card)] px-4 py-3.5',
              !p.isActive && 'opacity-60',
            )}
          >
            <div className="text-[22px] leading-none">{flag}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-[13px] font-bold text-[var(--foreground)]">
                  {p.name}
                </span>
                <Badge tone={p.statusTone} size="sm">
                  {p.statusLabel}
                </Badge>
              </div>
              <div className="mt-0.5 text-[11.5px] tabular-nums text-[var(--muted-foreground)]">
                {p.lastSyncRelative}
                {p.entitiesLast != null && p.entitiesLast > 0 && (
                  <> · {p.entitiesLast.toLocaleString('de-DE')} Entities</>
                )}
              </div>
            </div>
            <ToggleSwitch
              checked={p.isActive}
              disabled={isToggling}
              title={p.isActive ? 'Auto-Sync für dieses Profil aktiv' : 'Auto-Sync für dieses Profil aus'}
              onChange={(next) => onToggleActive(p.id, next)}
            />
            <SyncTypeSelect
              id={`sync-typ-${p.id}`}
              label={`Sync-Typ für ${p.name}`}
              wert={syncTypen[p.id] ?? SYNC_TYP_DEFAULT}
              onChange={(typ) => onSyncTypChange(p.id, typ)}
              disabled={laeuft || bulkSyncing}
            />
            <button
              type="button"
              onClick={() => onSync(p.id, syncTypen[p.id] ?? SYNC_TYP_DEFAULT)}
              disabled={laeuft || bulkSyncing || !p.isActive}
              /*
                DER GRUND FOLGT DEM ZEICHEN (Punkt A, R1): Beim ZEICHEN unten
                gewinnt die erste Stufe (`isSyncing`), beim `title` gewann
                vorher die zweite. Hielten beide Mengen dasselbe Profil und lief
                kein Sammellauf, zeigte der Knopf das Ladezeichen und behauptete
                im Tooltip zugleich, die Anfrage sei laengst beantwortet.
                Deshalb `&& !isSyncing`: Solange die Anfrage laeuft, gilt der
                Bestandstitel (E6 nimmt ihn ausdruecklich aus); erst danach
                nennt der Titel den Wartegrund. Die Reihenfolge davor
                (`!p.isActive` > `bulkSyncing`) bleibt unveraendert.
              */
              title={
                !p.isActive
                  ? 'Profil ist deaktiviert'
                  : bulkSyncing
                    ? 'Sammellauf laeuft — Einzel-Sync waehrenddessen gesperrt'
                    : wartetAufBestaetigung && !isSyncing
                      ? 'Sync gestartet – wartet auf Bestätigung durch die Historie'
                      : 'Manuellen Sync starten'
              }
              className={cn(
                'inline-flex h-7 min-w-[48px] shrink-0 items-center justify-center gap-1 rounded-md border border-[var(--border)] px-2.5 text-xs font-semibold text-[var(--foreground)] transition-colors',
                isSyncing || wartetAufBestaetigung
                  ? 'cursor-wait bg-[var(--muted)]'
                  : !p.isActive
                    ? 'cursor-not-allowed bg-[var(--card)] opacity-50'
                    : 'bg-[var(--card)] hover:bg-[var(--accent)]',
              )}
            >
              {isSyncing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : wartetAufBestaetigung ? (
                <span
                  role="img"
                  aria-label="Sync gestartet – wartet auf Bestätigung durch die Historie"
                  title="Sync gestartet – wartet auf Bestätigung durch die Historie"
                  className="inline-flex"
                >
                  <Hourglass className="h-3 w-3" aria-hidden="true" />
                </span>
              ) : (
                'Sync'
              )}
            </button>
          </div>
        )
      })}
    </div>
  )
}
